import { type SessionTimelineEntry } from '../../services/api/sessions';

const PRODUCT_CARD_REGEX = /\[PRODUCT_CARD\][\s\S]*?\[\/PRODUCT_CARD\]/g;

// Signerade bild-URL:er lever lika länge som filerna i Storage (30 dagar).
const CHAT_IMAGE_LINK_LIFETIME_DAYS = 30;

export const isChatImageLinkExpired = (uploadedAt?: string): boolean => {
  if (!uploadedAt) return false;
  const uploadedAtMs = new Date(uploadedAt).getTime();
  if (Number.isNaN(uploadedAtMs)) return false;
  const ageMs = Date.now() - uploadedAtMs;
  return ageMs > CHAT_IMAGE_LINK_LIFETIME_DAYS * 24 * 60 * 60 * 1000;
};

// Tidslinjen ritar korten med chattens egna komponenter. Flaggan skickar bara
// text, och där räcker det att veta ATT ett kort visades och för vilken order.
const ORDER_BLOCK_REGEX = /\[(ORDER_INFO|ORDER_LIST|ORDER_TRACKING|ORDER_CARD)\]([\s\S]*?)\[\/\1\]/g;

const ORDER_BLOCK_LABELS: Record<string, string> = {
  ORDER_INFO: 'Orderkort',
  ORDER_LIST: 'Orderlista',
  ORDER_TRACKING: 'Spårningskort',
  ORDER_CARD: 'Orderkort',
};

const summarizeOrderBlock = (kind: string, payload: string): string => {
  const label = ORDER_BLOCK_LABELS[kind] ?? kind;

  try {
    const parsed = JSON.parse(payload.trim()) as {
      view?: string;
      orderNumber?: string;
      orders?: Array<{ orderNumber?: string }>;
      statusText?: string;
      facts?: { method?: string };
    };
    // ORDER_CARD bär alltid en lista, även för ett enda kort.
    const onlyOrder =
      parsed.view && parsed.orders?.length === 1 ? parsed.orders[0]?.orderNumber : undefined;
    const details = [
      parsed.view,
      parsed.orderNumber ? `order ${parsed.orderNumber}` : '',
      onlyOrder ? `order ${onlyOrder}` : '',
      Array.isArray(parsed.orders) && !onlyOrder ? `${parsed.orders.length} ordrar` : '',
      parsed.facts?.method,
      parsed.statusText,
    ].filter(Boolean);

    return details.length ? `[${label}: ${details.join(' · ')}]` : `[${label}]`;
  } catch {
    return `[${label}]`;
  }
};

export const flagTextOf = (content: string): string =>
  content
    .replace(PRODUCT_CARD_REGEX, '')
    .replace(ORDER_BLOCK_REGEX, (_, kind: string, payload: string) =>
      summarizeOrderBlock(String(kind), String(payload)),
    )
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export const findLatestAssistantSequence = (timeline: SessionTimelineEntry[]): number | undefined =>
  [...timeline].reverse().find((entry) => entry.role === 'assistant')?.sequence;

// Flaggknappen skickar frågan och svaret som ett par. Frågan är den senaste
// användarturen FÖRE svaret — inte den senaste i sessionen.
export const mapPrecedingUserMessages = (timeline: SessionTimelineEntry[]): Map<number, string> => {
  const bySequence = new Map<number, string>();
  let lastUserContent = '';

  for (const entry of timeline) {
    if (entry.role === 'user') lastUserContent = entry.content;
    else if (entry.role === 'assistant') bySequence.set(entry.sequence, lastUserContent);
  }

  return bySequence;
};
